import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { User, UserStore } from '../../store/User';
import { Project, ProjectStore } from '../../store/Project';
import { PlanningTicket, PlanningTicketStore } from '../../store/planning';
import { DeveloperHoliday, DeveloperHolidayStore } from '../../store/planning';
import { Content } from 're-cy-cle';
import { ActionBtn, ACTION_ACCENTS } from './shared/ActionBtn';

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const WEEKS_TO_SHOW = 6;

function userColor(u: any): string {
    const name = (u?.fullName || u?.firstName || u?.email || '') as string;
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function projectColor(p: any): string {
    if ((p as any).color) return (p as any).color;
    const name = p?.name || '';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
}

function parseDateSafe(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date) return val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
}

const HOLIDAY_ICON: Record<string, string> = {
    holiday: '\uD83C\uDFD6\uFE0F',
    sick: '\uD83E\uDD12',
    conference: '\uD83C\uDF99\uFE0F',
    other: '\uD83D\uDCCC',
};

@observer
export default class DeveloperScheduleView extends React.Component<any, any> {
    @observable selectedUserId: number | null = null;
    @observable weekOffset = 0;
    @observable loading = false;

    userStore = new UserStore();
    projectStore = new ProjectStore({ relations: ['company'] });
    ticketStore = new PlanningTicketStore({ relations: ['milestone', 'assignee'] });
    holidayStore = new DeveloperHolidayStore();

    async componentDidMount() {
        await this.fetchAll();
    }

    @action async fetchAll() {
        this.loading = true;
        await Promise.all([
            this.userStore.fetch().catch((e: any) => console.warn('user fetch', e)),
            this.projectStore.fetch().catch((e: any) => console.warn('project fetch', e)),
            this.ticketStore.fetch().catch((e: any) => console.warn('ticket fetch', e)),
            this.holidayStore.fetch().catch((e: any) => console.warn('holiday fetch', e)),
        ]);
        if (!this.selectedUserId && this.users.length > 0) {
            this.selectedUserId = this.users[0].id;
        }
        this.loading = false;
    }

    get users(): User[] {
        return (this.userStore.models as User[]) || [];
    }

    get projects(): Project[] {
        return (this.projectStore.models as Project[] || []).filter(p => !p.hidden);
    }

    get tickets(): PlanningTicket[] {
        return (this.ticketStore.models as PlanningTicket[]) || [];
    }

    get holidays(): DeveloperHoliday[] {
        return (this.holidayStore.models as DeveloperHoliday[]) || [];
    }

    get selectedUser(): User | null {
        if (!this.selectedUserId) return null;
        return this.users.find(u => u.id === this.selectedUserId) || null;
    }

    getDevTickets(userId: number): PlanningTicket[] {
        return this.tickets.filter((t: any) => {
            const aid = t.assignee?.id ?? t.assignee;
            return Number(aid) === Number(userId);
        });
    }

    getDevHolidays(userId: number): DeveloperHoliday[] {
        return this.holidays.filter((h: any) => {
            const hid = h.user?.id ?? h.user;
            return Number(hid) === Number(userId);
        });
    }

    getTicketsForDay(userId: number, date: Date): PlanningTicket[] {
        return this.getDevTickets(userId).filter((t: any) => {
            const start = parseDateSafe(t.scheduledStart);
            const end = parseDateSafe(t.scheduledEnd) || start;
            if (!start) return false;
            return date >= start && date <= end;
        });
    }

    getHolidayForDay(userId: number, date: Date): DeveloperHoliday | null {
        return this.getDevHolidays(userId).find((h: any) => {
            const hs = parseDateSafe(h.startDate);
            const he = parseDateSafe(h.endDate);
            if (!hs || !he) return false;
            const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            return d >= hs && d <= he;
        }) || null;
    }

    getProjectById(id: any): Project | null {
        const pid = Number(id);
        return this.projects.find(p => Number(p.id) === pid) || null;
    }

    @action selectUser = (userId: number) => {
        this.selectedUserId = userId;
    };

    @action navWeek = (delta: number) => {
        this.weekOffset += delta;
    };

    @action goToToday = () => {
        this.weekOffset = 0;
    };

    renderWeeks(): React.ReactNode {
        if (!this.selectedUser) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const weeks: React.ReactNode[] = [];

        for (let w = 0; w < WEEKS_TO_SHOW; w++) {
            const weekStart = addDays(getStartOfWeek(today), this.weekOffset * 7 + w * 7);
            const days: React.ReactNode[] = [];

            for (let d = 0; d < 5; d++) {
                const date = addDays(weekStart, d);
                const isToday = isSameDay(date, today);
                const holiday = this.getHolidayForDay(this.selectedUser.id, date);
                const dayTickets = this.getTicketsForDay(this.selectedUser.id, date);

                days.push(
                    <div
                        key={'d-' + w + '-' + d}
                        data-test-dev-schedule-day-cell
                        style={{
                            flex: 1,
                            minHeight: 80,
                            border: '1px solid #e2e5e9',
                            borderRadius: 8,
                            padding: 6,
                            background: '#fff',
                            cursor: 'pointer',
                            ...(isToday ? { borderColor: '#2185d0', borderWidth: 2 } : {}),
                        }}
                    >
                        <div style={{
                            fontSize: 11, fontWeight: 600,
                            color: isToday ? '#2185d0' : '#6b7280',
                        }}>
                            {DAY_NAMES[d]} {date.getDate()}
                        </div>
                        {holiday && (
                            <div style={{ fontSize: 10, color: '#e53e3e', marginTop: 4 }}>
                                {HOLIDAY_ICON[(holiday as any).holidayType] || '\uD83D\uDCCC'} {(holiday as any).holidayType}
                            </div>
                        )}
                        {!holiday && dayTickets.map((ticket: any) => {
                            const proj = this.getProjectById(
                                (ticket.milestone as any)?.project?.id ??
                                (ticket.milestone as any)?.project
                            );
                            const color = proj ? projectColor(proj) : '#ccc';
                            return (
                                <div
                                    key={ticket.id}
                                    data-test-dev-schedule-ticket={ticket.id}
                                    style={{
                                        background: color, color: '#fff',
                                        fontSize: 10, padding: '2px 6px',
                                        borderRadius: 4, marginTop: 3,
                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={ticket.title}
                                >
                                    {ticket.title}
                                </div>
                            );
                        })}
                    </div>
                );
            }

            weeks.push(
                <div key={'w-' + w} style={{ marginBottom: 16 }}>
                    <div style={{
                        fontSize: 12, fontWeight: 600,
                        color: '#6b7280', marginBottom: 8,
                    }}>
                        {formatDate(weekStart)} — {formatDate(addDays(weekStart, 4))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {days}
                    </div>
                </div>
            );
        }

        return <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>{weeks}</div>;
    }

    render() {
        const u = this.selectedUser;
        if (!u) {
            return (
                <Content>
                    <div style={{ padding: 20, color: '#6b7280' }}>Select a developer to view their schedule.</div>
                </Content>
            );
        }

        return (
            <Content>
                <div data-test-dev-schedule-view style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '8px 16px', borderBottom: '1px solid #e2e5e9',
                        background: '#fff', flexShrink: 0, flexWrap: 'wrap',
                    }}>
                        <span style={{
                            background: userColor(u), width: 40, height: 40,
                            fontSize: 16, color: '#fff', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            {((u as any).fullName || (u as any).firstName || '?').charAt(0)}
                        </span>
                        <div style={{ flexGrow: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 16 }}>
                                {(u as any).fullName || ((u as any).firstName || '') + ' ' + ((u as any).lastName || '').trim()}
                            </div>
                        </div>
                        <select
                            data-test-dev-schedule-selector
                            value={this.selectedUserId || ''}
                            onChange={(e) => this.selectUser(Number(e.target.value))}
                            style={{
                                padding: '4px 8px', border: '1px solid #e2e5e9',
                                borderRadius: 4, fontSize: 13, background: '#fff',
                            }}
                        >
                            {this.users.map((user: any) => (
                                <option key={user.id} value={user.id}>
                                    {user.fullName || (user.firstName || '') + ' ' + (user.lastName || '').trim()}
                                </option>
                            ))}
                        </select>
                        <button
                            data-test-dev-schedule-prev
                            onClick={() => this.navWeek(-1)}
                            style={{ padding: '4px 8px', border: '1px solid #e2e5e9', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                        >
                            {'\u2039'}
                        </button>
                        <span style={{ fontSize: 13, fontWeight: 500, minWidth: 120, textAlign: 'center' }}>
                            {formatDate(addDays(getStartOfWeek(new Date()), this.weekOffset * 7))}
                        </span>
                        <button
                            data-test-dev-schedule-next
                            onClick={() => this.navWeek(1)}
                            style={{ padding: '4px 8px', border: '1px solid #e2e5e9', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                        >
                            {'\u203A'}
                        </button>
                        <ActionBtn
                            data-test-dev-schedule-today
                            onClick={this.goToToday}
                            accent={ACTION_ACCENTS.primary}
                        >
                            <span>Today</span>
                        </ActionBtn>
                    </div>
                    {this.renderWeeks()}
                </div>
            </Content>
        );
    }
}
